import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useCounts } from '@/hooks/useCounts';
import { useGetUserKey } from '@/hooks/useQueryKeys';
import {
  GraphQuery,
  MyResourceKey,
  QueryFilter,
  UniqueQueryKeys,
} from '@/types';
import { UseExtendQueryOptions, useQuery } from '@/utils/api';
import { sToMs } from '@/utils/date.util';

interface GroupedRow {
  fields: Record<string, unknown>;
  count: number;
}

interface GroupedResponse {
  groups: GroupedRow[];
  offset?: number;
}

/**
 * Loose GraphQuery type that accepts both the main types.ts GraphQuery
 * and the query builder's legacy variant (which uses `operator: string`
 * instead of the stricter `Operator` union type).
 *
 * Both produce identical JSON payloads at runtime — the difference is
 * purely in TypeScript's structural typing. This avoids forcing callers
 * to cast their query objects.
 */
type LooseGraphQuery =
  | GraphQuery
  | (Omit<GraphQuery, 'node'> & { node: object });

interface UseFilteredCountProps {
  resource: MyResourceKey;
  /** The active GraphQuery from the main data hook (same filters the table uses) */
  query: LooseGraphQuery | null | undefined;
}

interface UseFilteredCountResult {
  /** Total count of items matching the filters, undefined while loading */
  count: number | undefined;
  /** Whether the count query is currently loading */
  isLoading: boolean;
  /** Whether the count query errored */
  isError: boolean;
}

/**
 * Returns the `status STARTS WITH` prefixes when `query` filters on nothing but
 * status (no relationships, no other predicates). In that case the pre-computed
 * per-status counts equal the filtered total, so we can skip the Neo4j groupBy.
 * Any richer query returns null and falls back to groupBy.
 */
function statusPrefixesOnly(
  query: LooseGraphQuery | null | undefined
): string[] | null {
  const node = (query as GraphQuery | undefined)?.node;
  if (!node || node.relationships?.length) return null;

  const filters = (node.filters ?? []) as QueryFilter[];
  if (filters.length !== 1) return null;

  const filter = filters[0];
  if (
    filter.field !== 'status' ||
    filter.not ||
    filter.operator !== 'STARTS WITH'
  ) {
    return null;
  }

  const raw = Array.isArray(filter.value) ? filter.value : [filter.value];
  const prefixes = raw.filter((v): v is string => typeof v === 'string');
  return prefixes.length ? prefixes : null;
}

/**
 * Hook that returns the total count of items matching the active filters.
 *
 * Fast path (risk only): when the only filter is `status STARTS WITH [...]`
 * (the vulnerabilities page's default "Open" filter), it reads the pre-computed
 * per-status counts the count cron maintains via `/my/count` and sums the
 * matching buckets — no Neo4j scan.
 *
 * Fallback: any richer filter set sends a `groupBy: ["status"]` query with the
 * same filters and sums the returned group counts.
 */
export function useFilteredCount(
  { resource, query }: UseFilteredCountProps,
  options?: UseExtendQueryOptions<GroupedResponse> & { enabled?: boolean }
): UseFilteredCountResult {
  const axios = useAxios({});
  const parentEnabled = options?.enabled !== false;

  // Only `risk`'s /my/count returns a status-code → count map we can sum (the
  // AssetDrawer already consumes it). Assets default to a compound
  // status+visited filter no status bucket can answer, so they stay on groupBy.
  const statusPrefixes = useMemo(() => statusPrefixesOnly(query), [query]);
  const usePrecomputed =
    parentEnabled && resource === 'risk' && statusPrefixes !== null;

  const precomputed = useCounts({ resource }, { enabled: usePrecomputed });

  // groupBy fallback query: same filters, grouped to get counts.
  const countQuery = useMemo(() => {
    if (!query) return null;
    return {
      ...query,
      groupBy: ['status'],
      limit: 1000,
      page: 0,
      orderBy: undefined,
      descending: undefined,
      orderByFields: undefined,
    };
  }, [query]);

  const groupByEnabled =
    parentEnabled && countQuery !== null && !usePrecomputed;

  const groupBy = useQuery<GroupedResponse>({
    ...options,
    defaultErrorMessage: `Failed to fetch ${resource} count`,
    retry: 1,
    staleTime: sToMs(30),
    refetchInterval: groupByEnabled ? sToMs(60) : false,
    refetchIntervalInBackground: false,
    queryKey: useGetUserKey(
      [
        UniqueQueryKeys.MY,
        'filtered-count',
        resource,
        JSON.stringify(countQuery),
      ],
      false
    ),
    queryFn: async () => {
      if (!countQuery) {
        return { groups: [] };
      }
      const { data } = await axios({
        method: 'POST',
        url: '/my',
        params: { label: resource },
        data: countQuery,
      });
      return data as GroupedResponse;
    },
    enabled: groupByEnabled,
  });

  const count = useMemo(() => {
    if (usePrecomputed) {
      if (!precomputed.data || !statusPrefixes) return undefined;
      const buckets = precomputed.data as unknown as Record<string, unknown>;
      return Object.entries(buckets).reduce((sum, [bucket, value]) => {
        const matches =
          typeof value === 'number' &&
          statusPrefixes.some(p => bucket.startsWith(p));
        return matches ? sum + value : sum;
      }, 0);
    }

    if (!groupBy.data?.groups) return undefined;
    return groupBy.data.groups.reduce(
      (sum, group) => sum + (group.count || 0),
      0
    );
  }, [usePrecomputed, precomputed.data, statusPrefixes, groupBy.data]);

  return {
    count,
    isLoading: usePrecomputed ? precomputed.isLoading : groupBy.isLoading,
    isError: usePrecomputed ? precomputed.isError : groupBy.isError,
  };
}
